import React, { useState } from "react";
import { Calendar, Flag } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipContent } from "./tooltip";

// Types
interface SegmentedProgressProps {
  completedCheckPointCount: number;
  inProgressCheckPointCount: number;
  submittedCheckPointCount: number;
  totalCheckPoints: number;
  className?: string;
}

// Status color mappings
const statusColorMap = {
  completed: "bg-green-500",
  submitted: "bg-bits-blue",
  in_progress: "bg-bits-orange",
  pending: "bg-gray-400",
};

const statusBorderColorMap = {
  completed: "border-green-500",
  submitted: "border-bits-blue",
  in_progress: "border-bits-orange",
  pending: "border-gray-400",
};

const statusTextColorMap = {
  completed: "text-green-700",
  submitted: "text-bits-blue",
  in_progress: "text-bits-orange",
  pending: "text-gray-700",
};

const statusBgColorMap = {
  completed: "bg-green-100",
  submitted: "bg-blue-100",
  in_progress: "bg-orange-100",
  pending: "bg-gray-100",
};

const statusIconColorMap = {
  completed: "text-white",
  submitted: "text-white",
  in_progress: "text-white",
  pending: "text-white",
};

const statusDisplayNames = {
  completed: "Completed",
  submitted: "Submitted",
  in_progress: "In Progress",
  pending: "Pending",
};

const SegmentedProgress: React.FC<SegmentedProgressProps> = ({
  completedCheckPointCount,
  inProgressCheckPointCount,
  submittedCheckPointCount,
  totalCheckPoints,
  className = "",
}) => {
  if (totalCheckPoints <= 0) {
    return null;
  }

  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [tooltipSide, setTooltipSide] = useState<
    "top" | "bottom" | "left" | "right"
  >("top");

  // Calculate pending checkpoints
  const pendingCheckPointCount = Math.max(
    0,
    totalCheckPoints -
      completedCheckPointCount -
      inProgressCheckPointCount -
      submittedCheckPointCount,
  );

  // Create array of checkpoint statuses in order: completed -> submitted -> in_progress -> pending
  const checkpoints: Array<{
    status: "completed" | "submitted" | "in_progress" | "pending";
    index: number;
  }> = [];

  // Add completed checkpoints
  for (let i = 0; i < completedCheckPointCount; i++) {
    checkpoints.push({ status: "completed", index: checkpoints.length });
  }

  // Add submitted checkpoints
  for (let i = 0; i < submittedCheckPointCount; i++) {
    checkpoints.push({ status: "submitted", index: checkpoints.length });
  }

  // Add in-progress checkpoints
  for (let i = 0; i < inProgressCheckPointCount; i++) {
    checkpoints.push({ status: "in_progress", index: checkpoints.length });
  }

  // Add pending checkpoints
  for (let i = 0; i < pendingCheckPointCount; i++) {
    checkpoints.push({ status: "pending", index: checkpoints.length });
  }

  return (
    <div className={`relative flex items-center w-full gap-1 ${className}`}>
      {checkpoints.map((checkpoint, index) => (
        <React.Fragment key={index}>
          {/* Checkpoint Circle - circular flag icon */}
          <div className="relative group">
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={`w-8 h-8 rounded-full cursor-pointer transition-all duration-200 hover:scale-110 flex items-center justify-center ${
                    statusColorMap[checkpoint.status] || ""
                  } ${statusBorderColorMap[checkpoint.status]}`}
                  onPointerEnter={(e) => {
                    setHoveredIndex(index);
                    const rect = (
                      e.currentTarget as HTMLElement
                    ).getBoundingClientRect();
                    const spaceAbove = rect.top;
                    const spaceBelow = window.innerHeight - rect.bottom;
                    const spaceLeft = rect.left;
                    const spaceRight = window.innerWidth - rect.right;

                    // Prefer top/bottom; fallback to left/right
                    if (spaceAbove > spaceBelow + 20) setTooltipSide("top");
                    else if (spaceBelow > spaceAbove + 20)
                      setTooltipSide("bottom");
                    else if (spaceRight > spaceLeft) setTooltipSide("right");
                    else setTooltipSide("left");
                  }}
                  onPointerLeave={() => setHoveredIndex(null)}
                >
                  <Flag
                    size={16}
                    fill="white"
                    className={
                      statusIconColorMap[checkpoint.status] || "text-white"
                    }
                  />
                </div>
              </TooltipTrigger>

              <TooltipContent side={tooltipSide} sideOffset={8}>
                <div className="min-w-[140px]">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-xs font-semibold whitespace-nowrap">
                        Checkpoint {index + 1}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${statusBgColorMap[checkpoint.status]} ${statusTextColorMap[checkpoint.status]}`}
                      >
                        {statusDisplayNames[checkpoint.status]}
                      </span>
                    </div>
                  </div>
                </div>
              </TooltipContent>
            </Tooltip>
          </div>
        </React.Fragment>
      ))}
    </div>
  );
};

export default SegmentedProgress;
